import { faSpinner } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { twMerge } from 'tailwind-merge'
import { Icons } from '../assets/icons'
import { useInventory } from '../hooks/useInventory'
import { assetUrl, itemName } from '../utils/assetUrl'
import { ItemImage } from './ItemImage'

type InventoryRowProps = {
    nft: NFT
}

/**
 * The list-view counterpart to `InventoryCard`.
 *
 * The card is square and image-led, which is right for browsing but costs a lot of height: at
 * 375px the two-column grid fits about five items on screen. This row is ~72px, so the same screen
 * holds ten to eleven — the difference between scanning a collection and scrolling one.
 *
 * Everything the card shows is kept, just laid along the row instead of stacked: thumbnail, name,
 * legendary mark, rate, and the same activate control. The active/inactive language matches too —
 * a gradient rim means earning.
 */
export const InventoryRow = ({ nft }: InventoryRowProps) => {
    const { useStake, useUnstake } = useInventory()
    const { stake, isStaking } = useStake()
    const { unstake, isUnstaking } = useUnstake()

    const isActive = !!nft.staked
    const isLoading = isStaking || isUnstaking
    const label = itemName(nft)

    return (
        <div
            className={twMerge(
                'flex flex-row items-center gap-3 rounded overflow-hidden p-2',
                isActive ? 'border-gradient' : 'border border-white/20'
            )}
        >
            <div className='h-14 w-14 shrink-0 overflow-hidden rounded'>
                <ItemImage url={assetUrl(nft)} alt={label} />
            </div>

            <div className='flex min-w-0 flex-1 flex-col gap-0.5'>
                <div className='flex flex-row items-center gap-2'>
                    <span className='truncate font-black uppercase text-sm' title={label}>
                        {label}
                    </span>
                    {nft.is_legendary && (
                        <span
                            title='Legendary'
                            className='shrink-0 rounded-full bg-[#FFE500] px-1.5 text-2xs font-black text-black'
                        >
                            LEG
                        </span>
                    )}
                </div>
                {!!nft.stake_rate && (
                    <span
                        className={twMerge(
                            'flex flex-row items-center gap-1.5 text-xs font-bold',
                            isActive ? 'text-white/70' : 'text-white/40'
                        )}
                    >
                        <Icons.SkullPoint className='h-4 w-4 shrink-0' />
                        {nft.stake_rate} pts / day
                    </span>
                )}
            </div>

            {/*
              A switch, not a labelled button.

              This was an ON/OFF button, and ON/OFF reads as *state* rather than as an action — so
              an inactive row showed a bright "ON" and looked like the active one, while the row
              that really was earning sat next to a grey "OFF". Exactly backwards.

              A switch has no such ambiguity: lit and to the right means earning, and it is the
              control a phone user already knows. It also keeps the one rule the grid follows,
              that anything glowing is earning.
            */}
            <button
                role='switch'
                aria-checked={isActive}
                aria-label={`${label} — ${isActive ? 'active, tap to deactivate' : 'inactive, tap to activate'}`}
                disabled={isLoading}
                onClick={() => (isActive ? unstake([nft]) : stake([nft]))}
                className='flex min-h-[2.75rem] w-14 shrink-0 items-center justify-center disabled:opacity-50'
            >
                {isLoading ? (
                    <FontAwesomeIcon icon={faSpinner} spin />
                ) : (
                    <span
                        className={twMerge(
                            'relative h-6 w-11 rounded-full transition-colors',
                            isActive ? 'bg-br1-gradient' : 'bg-white/15'
                        )}
                    >
                        <span
                            className={twMerge(
                                'absolute top-0.5 h-5 w-5 rounded-full bg-white shadow transition-transform',
                                isActive ? 'left-0.5 translate-x-5' : 'left-0.5 translate-x-0'
                            )}
                        />
                    </span>
                )}
            </button>
        </div>
    )
}
